/**
 * Errors whose Sentry grouping must not depend on their full message.
 *
 * Messages routinely embed dynamic data (upstream exception reprs, gRPC error
 * details with timestamps, ids). Fingerprinting on the message therefore
 * creates one Sentry issue group per event (TV-1870). This base class splits
 * the message into a stable `context` (what failed, per throw site) and an
 * optional dynamic `detail` (why it failed), and derives the Sentry
 * fingerprint from the context alone.
 *
 * Design note (Template Method): this base class fixes the *algorithm* for
 * building the message and the fingerprint. Subclasses (see `CtxLoadError`,
 * `SubscriptionError` in store/service.ts) only vary identity (`name`) and
 * any extra payload fields — they never need to reimplement fingerprinting.
 * `getSentryFingerprint` below is the single place that reads the result, so
 * a brand-new error type opts into safe grouping just by extending this
 * class, with no changes needed anywhere else (e.g. `beforeSend`).
 */

export type FingerprintedErrorOptions = ErrorOptions & {
  /**
   * Dynamic detail appended to the message as `context: detail`.
   * This is the part that is allowed to vary per-event (upstream error
   * messages, ids, timestamps, ...). It is rendered into `message` for
   * humans/logs but is deliberately excluded from `sentryFingerprint` —
   * that's the whole point of this class.
   */
  detail?: string;
  /**
   * Explicit grouping key, overriding the default (which is just
   * `context`). Set this when:
   *  - several throw sites share the same human-readable `context` string
   *    but should still be grouped as distinct Sentry issues, or
   *  - you are re-wrapping an already-`FingerprintedError` and want the
   *    new wrapper to keep grouping under the *original* throw site's key
   *    instead of starting a new group at the wrapper's own context.
   */
  fingerprint?: string;
};

export class FingerprintedError extends Error {
  /**
   * Read by `getSentryFingerprint` to keep unrelated error classes in
   * separate fingerprint buckets. Subclasses override it with their own
   * class field; the base value matters only for direct instantiation,
   * where the inherited `Error` would otherwise report itself as "Error".
   */
  name = "FingerprintedError";

  /**
   * Stable Sentry grouping key for this error instance.
   * Invariant: must never contain variable data (timestamps, ids, upstream
   * exception reprs, etc.) — anything that changes between otherwise-identical
   * failures. Violating this invariant is exactly the TV-1870 bug: Sentry
   * would fingerprint per-event instead of per-failure-site.
   */
  readonly sentryFingerprint: string;

  /**
   * @param context Stable, human-readable description of *what* failed,
   *   fixed per throw site (e.g. "Unable to subscribe to service state").
   *   This becomes the default fingerprint, so keep it free of variable data.
   * @param options `cause`/`detail` describe *why* it failed (dynamic, safe
   *   to vary per event); `fingerprint` overrides the default grouping key.
   */
  constructor(context: string, options?: FingerprintedErrorOptions) {
    // Compose the human-facing message from the stable context plus the
    // optional dynamic detail — but keep them as separate fields internally
    // so the fingerprint can be derived from `context` alone, never from
    // the combined (and potentially variable) `message`.
    super(options?.detail !== undefined ? `${context}: ${options.detail}` : context, options);
    this.sentryFingerprint = options?.fingerprint ?? context;
  }
}

/**
 * Grouping key to give a wrapper error that re-throws `cause`.
 *
 * A `catch` block that wraps an arbitrary caught value in its own
 * `FingerprintedError` would otherwise flatten every distinct inner throw
 * site into the wrapper's single key. When the caught value already carries
 * a key, that key wins; `fallback` applies only to values that have none.
 */
export function inheritFingerprint(cause: unknown, fallback: string): string {
  return cause instanceof FingerprintedError ? cause.sentryFingerprint : fallback;
}

/**
 * Best-effort human-readable message from an unknown caught value. Unlike
 * `(e as Error).message`, this never yields `undefined` when something other
 * than an Error was thrown (strings, plain objects, ...) — `catch` clauses
 * in TS type their binding as `unknown`, and code that blindly casts to
 * `Error` silently produces "undefined" messages for non-Error throws.
 */
export function errorMessage(value: unknown): string {
  // Real Error (or subclass, including FingerprintedError) — use its message directly.
  if (value instanceof Error) return value.message;
  // A bare string was thrown (e.g. `throw "boom"`) — use it as-is.
  if (typeof value === "string") return value;
  try {
    // Anything else that was thrown (plain object, array, ...): try to render
    // it as JSON so the shape of the thrown value is still visible in logs.
    const json = JSON.stringify(value);
    // JSON.stringify(undefined) legitimately returns `undefined` (not a string,
    // and not a thrown error) — fall through to String() for that case too.
    if (json !== undefined) return json;
  } catch {
    // Circular references (or other values JSON can't serialize) throw here —
    // fall through to String() below rather than propagating a second error
    // out of an error-formatting helper.
  }
  // Last-resort stringification for primitives, circular objects, symbols, etc.
  return String(value);
}

/**
 * Stable Sentry fingerprint for an exception, or undefined if it should use
 * Sentry's default grouping. Intended for use in `beforeSend`.
 *
 * This is the single call site that needs to know about `FingerprintedError`
 * at all — `beforeSend` itself just calls this and applies the result, so it
 * never needs to enumerate every error subclass by name.
 */
export function getSentryFingerprint(exception: unknown): string[] | undefined {
  if (exception instanceof FingerprintedError) {
    // "{{ default }}" keeps Sentry's normal grouping salt (release, culprit,
    // etc.) in play; appending the error's `name` keeps unrelated error
    // classes from ever colliding in the same fingerprint bucket even if two
    // of them happened to pick the same `context` string.
    return ["{{ default }}", exception.name, exception.sentryFingerprint];
  }
  // Not one of ours — let Sentry fall back to its default grouping logic.
  return undefined;
}
